/**
 * useStripeDonationCheckout — wrapper réutilisable du checkout Stripe
 * Connect (Destination Charges) pour les pages de don de donoor.org.
 *
 * Pendant Stripe du composable `usePaypalDonationCheckout`. Encapsule :
 *   - L'instanciation de `useCheckout` provider="stripe"
 *   - La fonction `createPaymentIntent` (POST /api/public/transactions/create-payment)
 *   - Les `subscriptionHandlers` optionnels (SetupIntent + Subscription)
 *   - Le tracking du `payment_intent_id` pour `syncBuyerInfoFromWallet`
 *     (Apple Pay / Google Pay email récupéré post-clic)
 *
 * Utilisation depuis une page de don :
 *   ```ts
 *   const stripe = useStripeDonationCheckout({
 *     paymentForm,
 *     calculations,
 *     organizationId: donationData.organizationId,
 *     stripeAccountId: donationData.stripeAccountId,
 *     donateUs: donationData.donateUs,
 *     donateToUsAmount,
 *     contextIds: { qr_code_id: qrCodeId },
 *     enableSubscriptions: true,
 *     successUrl: (email) => `/donation-success?qrCodeId=${id}${email ? `&email=${email}` : ""}`,
 *   });
 *   // → stripe.checkout (useCheckout return), stripe.currentPaymentIntentId
 *   ```
 */

import { computed, ref, type Ref, type ComputedRef } from "vue";
import {
  useCheckout,
  type CreatePaymentIntentContext,
  type MoyenPaiementLocalPage,
  type PaymentIntentResult,
} from "@donoor/checkout";
import { generateIdempotencyKey } from "~/utils/idempotencyKey";
import { withDonationAmountParams } from "~/utils/donationSuccessUrl";
import { stripeDonationApi } from "~/services/api/donationCheckout";

/**
 * Vue minimale du composable `usePaymentForm` consommée par Stripe.
 */
interface PaymentFormSlice {
  form: Ref<{
    amount: string;
    donorEmail: string;
    donorName: string;
    message: string;
    isAnonymous: boolean;
  }>;
  includeFees: Ref<boolean>;
  isSubscription: Ref<boolean> | ComputedRef<boolean>;
  subscriptionPeriod: Ref<"weekly" | "monthly" | "yearly">;
}

/**
 * Vue minimale du composable `usePaymentCalculations` consommée par Stripe.
 */
interface PaymentCalculationsSlice {
  finalAmount: Ref<number> | ComputedRef<number>;
  amountWithFees: Ref<number> | ComputedRef<number>;
  /** Interval Stripe ("week" | "month" | "year") dérivé de subscriptionPeriod. */
  stripeInterval: Ref<string> | ComputedRef<string>;
}

export interface UseStripeDonationCheckoutOptions {
  /** Composable de form Donoor (`usePaymentForm()`). */
  paymentForm: PaymentFormSlice;
  /** Calculs dérivés (`usePaymentCalculations()`). */
  calculations: PaymentCalculationsSlice;
  /** Reactive de l'organizationId (issu de useDonationData/useCampaignDonationData). */
  organizationId: Ref<string | null> | ComputedRef<string | null>;
  /** Reactive du stripe_account_id Connect de l'org. */
  stripeAccountId: Ref<string | null> | ComputedRef<string | null>;
  /** Reactive du flag "Soutenir Donoor" (depuis useDonationData). */
  donateUs?: Ref<boolean> | ComputedRef<boolean>;
  /** Montant additionnel "Soutenir Donoor" si donateUs activé. */
  donateToUsAmount?: Ref<number> | ComputedRef<number>;
  /**
   * IDs métier optionnels passés en body au backend (le backend les utilise
   * pour relier la transaction au QR code / campagne).
   */
  contextIds?: {
    qr_code_id?: string;
    campaign_id?: number | string;
  };
  /**
   * Si `true`, monte les `subscriptionHandlers` Stripe (SetupIntent +
   * Subscription) pour les dons récurrents. Default `false` — à activer
   * seulement pour QR code et payment-link (jamais pour les cagnottes,
   * cf. memory/product_campaigns_no_recurring.md).
   */
  enableSubscriptions?: boolean;
  /**
   * Builder optionnel de champs supplémentaires à ajouter au body de
   * `/create-payment` et `/create-recurring-setup`. Utile pour les
   * champs page-spécifiques (ex: `custom_fields` JSON sur la page
   * payment-link). Le builder est appelé à chaque création d'intent
   * pour capter la valeur réactive au moment de l'appel.
   */
  extraBodyBuilder?: () => Record<string, unknown>;
  /** Construit l'URL de redirection après succès. */
  successUrl: (donorEmail?: string) => string;
  /** Couleur de l'association, transmise à la page de remerciement (« #RRGGBB »). */
  couleur?: () => string | null | undefined;
}

export function useStripeDonationCheckout(
  opts: UseStripeDonationCheckoutOptions,
) {
  const config = useRuntimeConfig();
  // Langue de la page : champs carte et messages Stripe/PayPal traduits.
  const { localeIntl } = useFormatLangue();
  const { t } = useI18n();
  // Note (ADR-001) : ce composable est un controller riche (Couche 2b).
  // Les appels HTTP sont délégués à `~/services/api/donationCheckout.ts`.
  // Le nom historique `useStripeDonationCheckout` est conservé car
  // l'ensemble du composable est déjà un contrat stable consommé par
  // 4 pages (/donate/[id], /pay/[slug], /c/[slug]/payment,
  // /campaigns/donate/[id]). Une renomination en `Controller` serait
  // un churn pur, sans bénéfice fonctionnel.

  /**
   * Tracking du PaymentIntent en cours — exposé pour permettre à la page
   * de l'utiliser dans le fallback WebSocket `onPaymentConfirmed`
   * (paymentSocket.joinPaymentRoom). Exposé en `Ref` plutôt qu'en `let`
   * pour conserver la réactivité.
   */
  const currentPaymentIntentId = ref<string | null>(null);

  /**
   * Apple Pay / Google Pay : si l'email vient du wallet (pas du form),
   * update PI metadata pour que le webhook puisse envoyer le reçu fiscal
   * au bon donateur. Non bloquant — webhook fallback sur billing_details.
   */
  async function syncBuyerInfoFromWallet(
    email: string,
    name?: string,
  ): Promise<void> {
    if (
      !email ||
      email === "anonymous@donoor.org" ||
      !currentPaymentIntentId.value
    )
      return;
    try {
      if (typeof window !== "undefined") {
        // Rattaché à CE paiement : la page de remerciement ne le réutilise
        // que pour lui (cf. emailMemoriseDuPaiement, useDonationSuccess).
        localStorage.setItem("donation_email", email);
        localStorage.setItem("donation_payment_intent", currentPaymentIntentId.value);
      }
      await stripeDonationApi.updateBuyerInfo({
        payment_intent_id: currentPaymentIntentId.value,
        donor_email: email,
        donor_name: name || undefined,
        organization_id: opts.organizationId.value,
      });
    } catch {
      // ignore
    }
  }

  /**
   * Builder du body de paiement, factorisé entre createPaymentIntent et
   * createSetupIntent (les deux endpoints partagent ces champs métier).
   */
  function buildPaymentBody(
    ctx: CreatePaymentIntentContext,
    donationType: "one_time" | "subscription",
  ): Record<string, unknown> {
    // ⚠️ donate_to_us TOUJOURS ajouté au montant chargé (même sans include_fees).
    // Sans ça, 10€ + 2€ pourboire → on ne charge que 10€. Karim 2026-06-28.
    const donateToUs = opts.donateToUsAmount?.value || 0;
    const amountToCharge = opts.paymentForm.includeFees.value
      ? opts.calculations.amountWithFees.value
      : opts.calculations.finalAmount.value + donateToUs;

    const body: Record<string, unknown> = {
      organization_id: opts.organizationId.value,
      amount: Math.round(amountToCharge * 100),
      net_amount: Math.round(opts.calculations.finalAmount.value * 100),
      currency: "eur",
      donation_type: donationType,
      subscription_period:
        donationType === "subscription"
          ? opts.paymentForm.subscriptionPeriod.value
          : null,
      anonymous: opts.paymentForm.form.value.isAnonymous,
      donor_email: ctx.donor.email || "anonymous@donoor.org",
      donor_name: !opts.paymentForm.form.value.isAnonymous
        ? ctx.donor.name || opts.paymentForm.form.value.donorName
        : null,
      message: opts.paymentForm.form.value.message || null,
      include_fees: opts.paymentForm.includeFees.value,
      stripe_account_id: opts.stripeAccountId.value,
      ...opts.contextIds,
      // Moyen local choisi (Bancontact d'une association belge) ; absent pour
      // la carte et les portefeuilles — la requête d'un don français ne change pas.
      ...(ctx.moyenPaiement ? { payment_method_type: ctx.moyenPaiement } : {}),
    };

    if (
      opts.donateUs?.value &&
      opts.donateToUsAmount?.value &&
      opts.donateToUsAmount.value > 0
    ) {
      body.donate_to_us = Math.round(opts.donateToUsAmount.value * 100);
    }

    // Champs additionnels page-spécifiques (ex: `custom_fields` JSON).
    // Mergés en dernier mais on garde les clés critiques de base : un
    // builder ne peut pas écraser organization_id, donation_type, etc.
    if (opts.extraBodyBuilder) {
      const extra = opts.extraBodyBuilder();
      for (const [key, value] of Object.entries(extra)) {
        if (!(key in body)) body[key] = value;
      }
    }

    return body;
  }

  /** Crée le PaymentIntent (don one-time). */
  async function createPaymentIntent(
    ctx: CreatePaymentIntentContext,
  ): Promise<PaymentIntentResult> {
    const response = await stripeDonationApi.createPaymentIntent(
      buildPaymentBody(ctx, "one_time"),
      generateIdempotencyKey(),
    );

    if (!response?.success || !response.data?.client_secret) {
      throw new Error(
        response?.message || t("don.checkoutErreurs.echecCreationPaiement"),
      );
    }

    currentPaymentIntentId.value = response.data.payment_intent_id;

    // Apple Pay : sync email wallet → PI metadata
    const formEmail = opts.paymentForm.form.value.donorEmail?.trim();
    const walletEmail = ctx.donor.email;
    if (
      walletEmail &&
      walletEmail !== formEmail &&
      walletEmail !== "anonymous@donoor.org"
    ) {
      syncBuyerInfoFromWallet(walletEmail, ctx.donor.name).catch(() => {});
    }

    return {
      clientSecret: response.data.client_secret,
      transactionId: response.data.payment_intent_id,
    };
  }

  /**
   * Subscription handlers (SetupIntent + Subscription) — montés seulement
   * si `enableSubscriptions` est true. Sinon `useCheckout` reçoit
   * `subscriptionHandlers: undefined` et le mode récurrent est désactivé.
   */
  const subscriptionHandlers = opts.enableSubscriptions
    ? {
        async createSetupIntent(ctx: CreatePaymentIntentContext) {
          const resp = await stripeDonationApi.createRecurringSetup(
            buildPaymentBody(ctx, "subscription"),
            generateIdempotencyKey(),
          );

          if (!resp?.success || !resp.data?.setup_secret) {
            throw new Error(resp?.message || t("don.checkoutErreurs.echecCreationSetupIntent"));
          }
          return {
            setupSecret: resp.data.setup_secret,
            setupIntentId: resp.data.setup_intent_id,
          };
        },
        async createSubscription(
          _ctx: CreatePaymentIntentContext,
          setupIntentId: string,
        ) {
          // Cf. buildPaymentBody : donate_to_us TOUJOURS ajouté.
          const donateToUs = opts.donateToUsAmount?.value || 0;
          const amountToCharge = opts.paymentForm.includeFees.value
            ? opts.calculations.amountWithFees.value
            : opts.calculations.finalAmount.value + donateToUs;

          const resp = await stripeDonationApi.createRecurring(
            {
              setup_intent_id: setupIntentId,
              organization_id: opts.organizationId.value,
              amount: Math.round(amountToCharge * 100),
              currency: "eur",
              interval: opts.calculations.stripeInterval.value,
              stripe_account_id: opts.stripeAccountId.value,
            },
            generateIdempotencyKey(),
          );

          if (!resp?.success) {
            throw new Error(resp?.message || t("don.checkoutErreurs.echecCreationSubscription"));
          }
          // Stripe API 2025+ : si la subscription est déjà "active" (paid
          // via default_payment_method), pas de client_secret retourné — le
          // provider skippera confirmCardPayment et redirigera direct.
          return {
            clientSecret: resp.data.client_secret || "",
            subscriptionId: resp.data.subscription_id,
            stripeAccountId: resp.data.stripe_account_id,
          };
        },
      }
    : undefined;

  // URL de succès enrichie des montants du don → la page /donation-success
  // affiche la confirmation instantanément (sans attendre le webhook).
  // Cf. ~/utils/donationSuccessUrl.ts pour le pourquoi complet.
  const successUrlWithAmounts = withDonationAmountParams(opts.successUrl, {
    couleur: opts.couleur,
    includeFees: opts.paymentForm.includeFees,
    finalAmount: opts.calculations.finalAmount,
    amountWithFees: opts.calculations.amountWithFees,
    donateToUsAmount: opts.donateToUsAmount,
    isSubscription: opts.paymentForm.isSubscription,
    interval: opts.calculations.stripeInterval,
  });

  const checkout = useCheckout({
    provider: "stripe",
    locale: localeIntl.value,
    publicKey: config.public.stripePublishableKey as string,
    // Getter (PAS `.value` figé) : le stripe_account_id de l'org est chargé de
    // façon asynchrone. Le résoudre à l'init du provider, sinon Stripe.js
    // s'initialise sur la plateforme → « No such payment_intent ».
    connectedAccountId: () => opts.stripeAccountId.value || undefined,
    currency: "eur",

    amount: computed(() => {
      const donateToUs = opts.donateToUsAmount?.value || 0;
      return opts.paymentForm.includeFees.value
        ? opts.calculations.amountWithFees.value
        : opts.calculations.finalAmount.value + donateToUs;
    }),
    donor: computed(() => ({
      email: opts.paymentForm.form.value.donorEmail,
      name: !opts.paymentForm.form.value.isAnonymous
        ? opts.paymentForm.form.value.donorName
        : undefined,
    })),
    recurring: opts.enableSubscriptions
      ? opts.paymentForm.isSubscription
      : ref(false),
    feesCovered: opts.paymentForm.includeFees,

    createPaymentIntent,
    subscriptionHandlers,

    successUrl: successUrlWithAmounts,
  });

  /**
   * Paie par un moyen LOCAL (Bancontact d'une association belge) : le
   * navigateur part vers la banque, qui ramène sur la page de remerciement.
   */
  function payerParMoyenLocal(moyen: MoyenPaiementLocalPage, nomTitulaire: string) {
    // Prélèvement SEPA (02/11/2026) : IBAN et mandat dans le formulaire de
    // Stripe déjà monté sous le bouton ; ponctuel ou mensuel selon ce formulaire.
    if (moyen === "sepa_debit") return checkout.confirmerPrelevementSepa({ nomTitulaire });
    return checkout.confirmerMoyenLocal(moyen, { nomTitulaire });
  }

  /** Formulaire de prélèvement SEPA (IBAN + mandat de Stripe), monté dans le panneau du moyen. */
  const prelevementSepa = {
    monter: (conteneur: string, mensuel: boolean) => checkout.monterPrelevementSepa(conteneur, { mensuel }),
    demonter: () => checkout.demonterPrelevementSepa(),
  };

  return {
    /** useCheckout instance Stripe (mount, confirm, etc.) */
    checkout,
    payerParMoyenLocal,
    prelevementSepa,
    /**
     * ID du PaymentIntent en cours (Ref). Exposé pour
     * `paymentSocket.joinPaymentRoom(...)` après confirmCardPayment.
     */
    currentPaymentIntentId,
  };
}
